/**
 * AI Log Page
 * Visar alla AI-förfrågningar som gjorts i systemet
 */

import React, { useState, useEffect, useCallback } from 'react';
import { ROUTES } from '@/constants/routes';
import { useNavigate } from 'react-router-dom';
import { 
 ArrowLeftIcon,
 SparklesIcon, 
 CheckCircleIcon, 
 XCircleIcon,
 ClockIcon,
 UserIcon,
 ChevronLeftIcon,
 ChevronRightIcon,
 EyeIcon
} from '@heroicons/react/24/outline';
import { useTranslation } from '@/store/languageStore';
import { useAuth } from '@/store/authStore';
import api from '@/services/api/client';

interface Organization {
 organization_id: string;
 name: string;
}

interface AILogEntry {
 id: number;
 userId: string;
 userName: string;
 userEmail: string;
 organizationId: string;
 organizationName: string;
 requestType: string;
 question: string;
 responsePreview: string;
 responseTimeMs: number;
 model: string;
 success: boolean;
 errorMessage?: string;
 createdAt: string;
}

interface AILogDetails extends AILogEntry {
 context?: string;
 response: string;
 tokensUsed?: number;
 ipAddress: string;
 userAgent: string;
}

const AILog: React.FC = () => {
 const navigate = useNavigate();
 const { t } = useTranslation();
 const { user } = useAuth();
 const [logs, setLogs] = useState<AILogEntry[]>([]);
 const [organizations, setOrganizations] = useState<Organization[]>([]);
 const [total, setTotal] = useState(0);
 const [isLoading, setIsLoading] = useState(true);
 const [error, setError] = useState<string | null>(null);
 const [page, setPage] = useState(0);
 const [pageSize] = useState(20);
 const [selectedLog, setSelectedLog] = useState<AILogDetails | null>(null);
 const [showModal, setShowModal] = useState(false);
 
 // Filters
 const [selectedOrgId, setSelectedOrgId] = useState<string>('');
 const [selectedType, setSelectedType] = useState<string>('');
 const [selectedSuccess, setSelectedSuccess] = useState<string>('');

 // Ladda organisationer vid mount
 const loadOrganizations = useCallback(async () => {
 try {
 const response = await api.get('/api/organizations');
 if (response.data.success && response.data.data) {
 setOrganizations(response.data.data);
 }
 } catch {
 // Failed to load organizations - handled silently
 }
 }, []);

 useEffect(() => {
 loadOrganizations();
 }, [loadOrganizations]);

 useEffect(() => {
 loadLogs();
 }, [page, selectedOrgId, selectedType, selectedSuccess]);

 const loadLogs = async () => {
 setIsLoading(true);
 setError(null);

 try {
 const params = new URLSearchParams();
 params.append('limit', pageSize.toString());
 params.append('offset', (page * pageSize).toString());
 
 if (selectedOrgId) params.append('organizationId', selectedOrgId);
 if (selectedType) params.append('requestType', selectedType);
 if (selectedSuccess) params.append('success', selectedSuccess);

 const response = await fetch(`/api/ai/logs?${params.toString()}`, {
 credentials: 'include'
 });

 const data = await response.json();

 if (data.success) {
 setLogs(data.data.logs);
 setTotal(data.data.total);
 } else {
 setError(data.error);
 }
 } catch (err: any) {
 setError(err.message);
 } finally {
 setIsLoading(false);
 }
 };

 const loadLogDetails = async (id: number) => {
 try {
 const response = await fetch(`/api/ai/logs/${id}`, {
 credentials: 'include'
 });

 const data = await response.json();

 if (data.success) {
 setSelectedLog(data.data);
 setShowModal(true);
 }
 } catch {
 // Failed to load log details - handled silently
 }
 };

 const requestTypes = [
 { value: 'chat', label: t('ai.log.types.chat') },
 { value: 'analyze_vehicle', label: t('ai.log.types.analyze_vehicle') },
 { value: 'analyze_driver', label: t('ai.log.types.analyze_driver') },
 { value: 'analyze_fleet', label: t('ai.log.types.analyze_fleet') },
 { value: 'compare_vehicles', label: t('ai.log.types.compare_vehicles') },
 { value: 'explain_metric', label: t('ai.log.types.explain_metric') },
 ];

 const getTypeLabel = (type: string) => {
 const found = requestTypes.find(t => t.value === type);
 return found ? found.label : type;
 };

 const formatDate = (dateString: string) => {
 return new Date(dateString).toLocaleString('sv-SE', {
 year: 'numeric',
 month: '2-digit',
 day: '2-digit',
 hour: '2-digit',
 minute: '2-digit'
 });
 };

 const totalPages = Math.ceil(total / pageSize);

 return (
 <div className="min-h-screen bg-bgLight p-6">
 <div className="max-w-7xl mx-auto">
 {/* Header */}
 <div className="mb-6">
 <div className="flex items-center gap-3 mb-2">
 <button onClick={() => navigate(ROUTES.SYSADMIN)} className="p-2 hover:bg-bgElevated rounded-lg transition-colors">
 <ArrowLeftIcon className="w-5 h-5 text-textDefault" />
 </button>
 <h1 className="text-2xl font-bold text-textDefault flex items-center gap-2">
 <SparklesIcon className="w-6 h-6 text-primary" />
 {t('ai.log.title')}
 </h1>
 </div>
 <p className="text-textSecondary">
 {t('ai.log.description')}
 </p>
 </div>

 {/* Filters */}
 <div className="bg-bgElevated rounded-lg shadow-sm border border-border p-4 mb-6">
 <div className="flex flex-wrap items-center gap-4">
 {/* Organisation */}
 {user?.is_global_admin && (
 <div className="min-w-[200px]">
 <label className="block text-sm font-medium text-textDefault mb-1">
 {t('ai.log.organization')}
 </label>
 <select
 value={selectedOrgId}
 onChange={(e) => { setSelectedOrgId(e.target.value); setPage(0); }}
 className="w-full rounded-lg border border-border bg-bgBase px-3 py-2 text-sm"
 >
 <option value="">{t('common.all')}</option>
 {organizations?.map((org: { organization_id: string; name: string }) => (
 <option key={org.organization_id} value={org.organization_id}>
 {org.name}
 </option>
 ))}
 </select>
 </div>
 )}

 {/* Typ */}
 <div className="min-w-[180px]">
 <label className="block text-sm font-medium text-textDefault mb-1">
 {t('ai.log.requestType')}
 </label>
 <select
 value={selectedType}
 onChange={(e) => { setSelectedType(e.target.value); setPage(0); }}
 className="w-full rounded-lg border border-border bg-bgBase px-3 py-2 text-sm"
 >
 <option value="">{t('common.all')}</option>
 {requestTypes.map(type => (
 <option key={type.value} value={type.value}>{type.label}</option>
 ))}
 </select>
 </div>

 {/* Status */}
 <div className="min-w-[140px]">
 <label className="block text-sm font-medium text-textDefault mb-1">
 {t('ai.log.success')}
 </label>
 <select
 value={selectedSuccess}
 onChange={(e) => { setSelectedSuccess(e.target.value); setPage(0); }}
 className="w-full rounded-lg border border-border bg-bgBase px-3 py-2 text-sm"
 >
 <option value="">{t('common.all')}</option>
 <option value="true">{t('common.success')}</option>
 <option value="false">{t('common.error')}</option>
 </select>
 </div>

 {/* Totalt */}
 <div className="ml-auto text-sm text-textSecondary">
 {total} {t('common.results')}
 </div>
 </div>
 </div>

 {/* Error */}
 {error && (
 <div className="mb-6 p-4 bg-red-50 dark:bg-red-900/30 border border-red-200 dark:border-red-800 rounded-lg text-red-600 dark:text-red-400">
 {error}
 </div>
 )}

 {/* Table */}
 <div className="bg-bgElevated rounded-lg shadow-sm border border-border overflow-hidden">
 <div className="overflow-x-auto">
 <table className="min-w-full divide-y divide-border">
 <thead className="bg-bgLight">
 <tr>
 <th className="px-4 py-3 text-left text-xs font-medium text-textSecondary uppercase tracking-wider">
 {t('ai.log.createdAt')}
 </th>
 <th className="px-4 py-3 text-left text-xs font-medium text-textSecondary uppercase tracking-wider">
 {t('ai.log.user')}
 </th>
 <th className="px-4 py-3 text-left text-xs font-medium text-textSecondary uppercase tracking-wider">
 {t('ai.log.requestType')}
 </th>
 <th className="px-4 py-3 text-left text-xs font-medium text-textSecondary uppercase tracking-wider">
 {t('ai.log.question')}
 </th>
 <th className="px-4 py-3 text-left text-xs font-medium text-textSecondary uppercase tracking-wider">
 {t('ai.log.responseTime')}
 </th>
 <th className="px-4 py-3 text-left text-xs font-medium text-textSecondary uppercase tracking-wider">
 {t('ai.log.success')}
 </th>
 <th className="px-4 py-3 text-right text-xs font-medium text-textSecondary uppercase tracking-wider">
 
 </th>
 </tr>
 </thead>
 <tbody className="bg-bgElevated divide-y divide-border">
 {isLoading ? (
 <tr>
 <td colSpan={7} className="px-4 py-8 text-center text-textTertiary">
 {t('common.loading')}
 </td>
 </tr>
 ) : logs.length === 0 ? (
 <tr>
 <td colSpan={7} className="px-4 py-8 text-center text-textTertiary">
 {t('ai.log.noLogs')}
 </td>
 </tr>
 ) : (
 logs.map((log) => (
 <tr key={log.id} className="hover:bg-bgBase/50">
 <td className="px-4 py-3 whitespace-nowrap text-sm text-textSecondary">
 {formatDate(log.createdAt)}
 </td>
 <td className="px-4 py-3 whitespace-nowrap">
 <div className="flex items-center gap-2">
 <UserIcon className="h-4 w-4 text-textTertiary" />
 <span className="text-sm text-textDefault">
 {log.userName || log.userEmail || log.userId}
 </span>
 </div>
 </td>
 <td className="px-4 py-3 whitespace-nowrap">
 <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-indigo-100 text-indigo-800 dark:bg-indigo-900 dark:text-indigo-200">
 {getTypeLabel(log.requestType)}
 </span>
 </td>
 <td className="px-4 py-3 max-w-xs truncate text-sm text-textSecondary">
 {log.question}
 </td>
 <td className="px-4 py-3 whitespace-nowrap text-sm text-textSecondary">
 <div className="flex items-center gap-1">
 <ClockIcon className="h-4 w-4 text-textTertiary" />
 {log.responseTimeMs ? `${(log.responseTimeMs / 1000).toFixed(1)}s` : '-'}
 </div>
 </td>
 <td className="px-4 py-3 whitespace-nowrap">
 {log.success ? (
 <CheckCircleIcon className="h-5 w-5 text-green-500" />
 ) : (
 <XCircleIcon className="h-5 w-5 text-red-500" />
 )}
 </td>
 <td className="px-4 py-3 whitespace-nowrap text-right">
 <button
 onClick={() => loadLogDetails(log.id)}
 className="text-indigo-600 hover:text-indigo-800 dark:text-indigo-400 dark:hover:text-indigo-300"
 title={t('ai.log.viewDetails')}
 >
 <EyeIcon className="h-5 w-5" />
 </button>
 </td>
 </tr>
 ))
 )}
 </tbody>
 </table>
 </div>

 {/* Pagination */}
 {totalPages > 1 && (
 <div className="flex items-center justify-between px-4 py-3 border-t border-border">
 <div className="text-sm text-textSecondary">
 {page * pageSize + 1} - {Math.min((page + 1) * pageSize, total)} av {total}
 </div>
 <div className="flex items-center gap-2">
 <button
 onClick={() => setPage(p => Math.max(0, p - 1))}
 disabled={page === 0}
 className="p-1 rounded hover:bg-bgBase disabled:opacity-50"
 >
 <ChevronLeftIcon className="h-5 w-5" />
 </button>
 <span className="text-sm text-textSecondary">
 {page + 1} / {totalPages}
 </span>
 <button
 onClick={() => setPage(p => Math.min(totalPages - 1, p + 1))}
 disabled={page >= totalPages - 1}
 className="p-1 rounded hover:bg-bgBase disabled:opacity-50"
 >
 <ChevronRightIcon className="h-5 w-5" />
 </button>
 </div>
 </div>
 )}
 </div>

 {/* Detail Modal */}
 {showModal && selectedLog && (
 <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
 <div className="bg-bgElevated rounded-lg shadow-xl max-w-4xl w-full max-h-[90vh] overflow-hidden">
 <div className="flex items-center justify-between px-6 py-4 border-b border-border">
 <h2 className="text-lg font-semibold text-textDefault">
 {t('copy.admin.aiRequestTitle', { id: selectedLog.id })}
 </h2>
 <button
 onClick={() => setShowModal(false)}
 className="text-textTertiary hover:text-textSecondary"
 >
 ✕
 </button>
 </div>
 
 <div className="p-6 overflow-y-auto max-h-[calc(90vh-120px)]">
 <div className="grid grid-cols-2 gap-4 mb-6">
 <div>
 <label className="text-sm font-medium text-textSecondary">
 {t('ai.log.user')}
 </label>
 <p className="text-textDefault">
 {selectedLog.userName || selectedLog.userEmail}
 </p>
 </div>
 <div>
 <label className="text-sm font-medium text-textSecondary">
 {t('ai.log.organization')}
 </label>
 <p className="text-textDefault">
 {selectedLog.organizationName || '-'}
 </p>
 </div>
 <div>
 <label className="text-sm font-medium text-textSecondary">
 {t('ai.log.requestType')}
 </label>
 <p className="text-textDefault">
 {getTypeLabel(selectedLog.requestType)}
 </p>
 </div>
 <div>
 <label className="text-sm font-medium text-textSecondary">
 {t('ai.log.responseTime')}
 </label>
 <p className="text-textDefault">
 {selectedLog.responseTimeMs ? `${(selectedLog.responseTimeMs / 1000).toFixed(2)} sekunder` : '-'}
 </p>
 </div>
 <div>
 <label className="text-sm font-medium text-textSecondary">
 {t('ai.log.createdAt')}
 </label>
 <p className="text-textDefault">
 {formatDate(selectedLog.createdAt)}
 </p>
 </div>
 <div>
 <label className="text-sm font-medium text-textSecondary">
 {t('ai.log.success')}
 </label>
 <p className={selectedLog.success ? 'text-green-600' : 'text-red-600'}>
 {selectedLog.success ? t('common.success') : t('common.error')}
 </p>
 </div>
 </div>

 <div className="mb-4">
 <label className="text-sm font-medium text-textSecondary block mb-2">
 {t('ai.log.question')}
 </label>
 <div className="bg-bgLight rounded-lg p-4 text-textDefault whitespace-pre-wrap">
 {selectedLog.question}
 </div>
 </div>

 {selectedLog.context && (
 <div className="mb-4">
 <label className="text-sm font-medium text-textSecondary block mb-2">
 Kontext
 </label>
 <div className="bg-bgLight rounded-lg p-4 text-textDefault whitespace-pre-wrap text-sm">
 {selectedLog.context}
 </div>
 </div>
 )}

 <div className="mb-4">
 <label className="text-sm font-medium text-textSecondary block mb-2">
 {t('ai.log.response')}
 </label>
 <div className="bg-indigo-50 dark:bg-indigo-900/30 rounded-lg p-4 text-textDefault whitespace-pre-wrap">
 {selectedLog.response || selectedLog.errorMessage || '-'}
 </div>
 </div>

 {selectedLog.ipAddress && (
 <div className="text-sm text-textSecondary">
 IP: {selectedLog.ipAddress}
 </div>
 )}
 </div>
 </div>
 </div>
 )}
 </div>
 </div>
 );
};

export default AILog;
